import { useCallback, useEffect, useMemo } from "react";
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { ModuleLayout } from "../../layout/ModuleLayout";
import {
  Avatar,
  Icon,
  ModuleListItem,
  TOPBAR_AVATAR_SIZE,
  TopBarHeader,
} from "../../components/ui";
import { ListPane } from "../../components/layout/ListPane";
import { ModuleListContent } from "../../components/layout/ModuleListContent";
import { DetailPane } from "../../components/layout/DetailPane";
import { useModuleList } from "../../hooks/useModuleList";
import { useEntityContextMenu } from "../../hooks/useEntityContextMenu";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { EntityDetailTabs } from "./EntityDetailTabs";
import { BaseListItem } from "./BaseListItem";
import { ListPaneHeaderActions } from "./ListPaneHeaderActions";
import { useModuleRouter } from "./useModuleRouter";
import { useEntityRename, useListRenameState } from "./useEntityRename";
import type { ListItem, ModuleConfig, ModuleQueryKeys } from "./types";
import type { FacetSummary, LinkedEntitySummary } from "./sharedTypes";

interface DetailResponse {
  readonly id: string;
  readonly name: string | null;
  readonly linked_entities: readonly LinkedEntitySummary[];
  readonly facets: readonly FacetSummary[];
  readonly canonical?: Record<string, unknown>;
}

export interface BaseModuleComponentProps {
  readonly config: ModuleConfig;
  readonly queryKeys: ModuleQueryKeys;
}

export function BaseModuleComponent({
  config,
  queryKeys,
}: BaseModuleComponentProps): JSX.Element {
  const runtime = useAppRuntime();
  const { selectedId: routerSelectedId, setSelectedId: setRouterSelectedId } =
    useModuleRouter(config.id, config.primaryEntityType);

  const rpcList = config.rpc?.list ?? `${config.id}.list`;
  const rpcGet = config.rpc?.get ?? `${config.id}.get`;
  const rpcUpdate = config.rpc?.update;

  // ── List ──
  const list = useModuleList<Record<string, unknown>, ListItem>({
    rpcMethod: rpcList,
    queryKeyBase: queryKeys.all,
    mapItem: config.mapListItem ?? defaultMapListItem,
    getId: (item) => item.id,
    extraParams: config.rpcListParams,
  });

  // ── Selection: router is source of truth, useModuleList auto-selects first ──
  const selectedId = routerSelectedId ?? list.selectedId;

  // Sync useModuleList auto-selection → router (first item auto-select)
  useEffect(() => {
    if (!routerSelectedId && list.selectedId) {
      setRouterSelectedId(list.selectedId);
    }
  }, [routerSelectedId, list.selectedId, setRouterSelectedId]);

  // Combined setter: updates both router + list internal state
  const setSelectedId = useCallback(
    (id: string | undefined) => {
      setRouterSelectedId(id);
      list.setSelectedId(id);
    },
    [setRouterSelectedId, list],
  );

  const selectedMissingFromList = !!selectedId && !list.items.some((item) => item.id === selectedId);

  const { data: selectedListItemRaw } = useQuery({
    queryKey: [...queryKeys.detail(selectedId ?? ""), "selected-list-item"],
    queryFn: () =>
      runtime.transport.rpc<Record<string, unknown>>(rpcGet, {
        id: selectedId,
      }),
    enabled: selectedMissingFromList,
    staleTime: 15_000,
  });
  const selectedListItem = useMemo(
    () => (
      selectedListItemRaw
        ? (config.mapListItem ?? defaultMapListItem)(selectedListItemRaw)
        : undefined
    ),
    [config.mapListItem, selectedListItemRaw],
  );
  const visibleItems = useMemo(
    () => (
      selectedListItem && selectedId
        ? [selectedListItem, ...list.items.filter((item) => item.id !== selectedId)]
        : list.items
    ),
    [list.items, selectedId, selectedListItem],
  );

  // ── Detail query ──
  const { data: detail } = useQuery({
    queryKey: queryKeys.detail(selectedId ?? ""),
    queryFn: () =>
      runtime.transport.rpc<DetailResponse>(rpcGet, {
        id: selectedId,
      }),
    enabled: !!selectedId && config.detailType !== "custom",
    staleTime: 15_000,
  });

  // ── Agent context ──
  useEffect(() => {
    if (!selectedId) {
      runtime.agent.setActiveContext(null);
      return;
    }
    const item = visibleItems.find((i) => i.id === selectedId);
    const title = item?.name ?? config.title;
    runtime.agent.setActiveContext({
      moduleId: config.id,
      entityId: selectedId,
      entityTitle: title,
      // Telegram: the entity IS the chat — propagate chatId/chatTitle for AgentPanel
      ...(config.id === "telegram" ? { chatId: selectedId, chatTitle: title } : {}),
    });
  }, [selectedId, visibleItems, runtime, config.id, config.title]);

  // ── Rename (shared mutation for both header and list) ──
  const patchItemName = useCallback(
    (id: string, name: string) => { list.patchItem(id, { name } as Partial<ListItem>); },
    [list],
  );

  const entityRename = useEntityRename({
    rpcMethod: rpcUpdate,
    entityId: selectedId,
    queryKeys,
    runtime,
    mapParams: config.mapRenameParams,
    onPatchItem: patchItemName,
  });

  // ── List rename editing state ── @tested-by: tst_fe_scn_list_rename_001
  const listRename = useListRenameState({
    renameEntity: entityRename.renameEntity,
  });

  const listRenameEnabled = !!(config.enableListRename && rpcUpdate);

  // ── Context menu ──
  const ctx = useEntityContextMenu<ListItem>({
    entityId: (item) => item.id,
    moduleItems: config.contextMenuItems,
    isPinned: (item) => item.is_pinned === true,
    onRename: listRenameEnabled
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      ? (item) => { listRename.startRename(item.id); }
      : undefined,
  });

  // ── Selected item for header ──
  const selectedItem = selectedId
    ? visibleItems.find((i) => i.id === selectedId)
    : undefined;

  // ── Render ──
  const descriptionSchema =
    config.descriptionSchema ?? `${config.id}.description`;
  const memorySchema = config.memorySchema ?? `${config.id}.memory`;

  return (
    <>
    {ctx.menu}
    <ModuleLayout
      moduleName={config.title}
      rightPane={config.RightPaneComponent ? (
        <config.RightPaneComponent selectedId={selectedId} runtime={runtime} />
      ) : undefined}
      listPane={
          <ListPane
            title={config.title}
            headerActions={
              <ListPaneHeaderActions
                runtime={runtime}
                icon={config.headerActionIcon}
                onAction={config.onHeaderAction}
                onCreated={(id) => { setSelectedId(id); }}
                invalidateKeys={queryKeys.all}
                CustomComponent={config.HeaderActions}
              />
            }
            onSearch={list.setSearchQuery}
          >
            <ModuleListContent
              items={visibleItems}
              isLoading={list.isLoading}
              hasMore={list.hasMore}
              searchQuery={list.searchQuery}
              onLoadMore={list.loadMore}
              selectedId={selectedId}
              onSelect={setSelectedId}
              getId={(item) => item.id}
              groupBy={config.groupBy}
              getGroupDate={config.getGroupDate}
              getGroupLetter={config.getGroupLetter}
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              isPinned={config.groupBy ? (item) => item.is_pinned === true : undefined}
              renderItem={(item, selected) => (
                <ModuleListItem
                  selected={selected}
                  onContextMenu={(e) => { ctx.open(e, item); }}
                >
                  {/* @tested-by: tst_fe_scn_list_rename_001, tst_fe_scn_list_rename_004 */}
                  {listRename.editingItemId === item.id ? (
                    <>
                      <Avatar
                        label={initials(item.name)}
                        color={config.themeColor}
                        size="md"
                        imageSrc={item.avatar_url ?? undefined}
                      />
                      <input
                        data-testid="list-rename-input"
                        className="flex-1 min-w-0 bg-transparent text-[13px] font-medium text-content outline-none"
                        defaultValue={item.name ?? ""}
                        autoFocus
                        onFocus={(e) => { e.target.select(); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            listRename.commitRename(item.id, item.name, e.currentTarget.value);
                          }
                          if (e.key === "Escape") {
                            listRename.cancelRename();
                          }
                        }}
                        onClick={(e) => { e.stopPropagation(); }}
                      />
                      <button
                        type="button"
                        data-testid="list-rename-cancel"
                        className="shrink-0 p-0.5 rounded text-content-tertiary hover:text-content cursor-pointer"
                        onMouseDown={(e) => { e.preventDefault(); listRename.cancelRename(); }}
                      >
                        <Icon name="close" size={14} />
                      </button>
                      <button
                        type="button"
                        data-testid="list-rename-confirm"
                        className="shrink-0 p-0.5 rounded text-content-tertiary hover:text-content cursor-pointer"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const input = e.currentTarget.parentElement?.querySelector("input");
                          if (input) listRename.commitRename(item.id, item.name, input.value);
                        }}
                      >
                        <Icon name="check" size={14} />
                      </button>
                    </>
                  ) : config.ListItemContent ? (
                    <config.ListItemContent item={item} selected={selected} />
                  ) : (
                    <BaseListItem item={item} themeColor={config.themeColor} />
                  )}
                </ModuleListItem>
              )}
            />
          </ListPane>
      }
      detailPane={
        config.detailType === "custom" && config.DetailPanel && selectedId ? (
          // Custom detail — no wrapper header, DetailPanel manages its own layout
          // Always render if selectedId exists — panel fetches its own data
          <config.DetailPanel
            entityId={selectedId}
            moduleId={config.id}
            runtime={runtime}
          />
        ) : selectedItem ? (
          <DetailPane
            headerNode={
              config.HeaderComponent ? (
                <config.HeaderComponent
                  entityId={selectedId}
                  entityName={selectedItem.name}
                  moduleId={config.id}
                  themeColor={config.themeColor}
                  runtime={runtime}
                  onRename={rpcUpdate ? entityRename.rename : undefined}
                />
              ) : (
                <TopBarHeader
                  leading={
                    <Avatar
                      label={initials(selectedItem.name)}
                      color={config.themeColor}
                      size={TOPBAR_AVATAR_SIZE}
                      imageSrc={selectedItem.avatar_url ?? undefined}
                    />
                  }
                  title={selectedItem.name ?? "Untitled"}
                  onTitleEdit={rpcUpdate ? entityRename.rename : undefined}
                  subtitle={selectedItem.preview ?? undefined}
                />
              )
            }
          >
            {config.DetailPanel ? (
              <config.DetailPanel
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entityId={selectedId!}
                moduleId={config.id}
                runtime={runtime}
              />
            ) : (
              <EntityDetailTabs
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entityId={selectedId!}
                linkedEntities={detail?.linked_entities ?? []}
                facets={detail?.facets ?? []}
                descriptionSchemaId={descriptionSchema}
                memorySchemaId={memorySchema}
                searchable
                maxVisibleTabs={7}
                detailsContent={
                  config.DetailsTabContent &&
                  (config.shouldShowDetailsTab?.({
                    facets: detail?.facets ?? [],
                    linkedEntities: detail?.linked_entities ?? [],
                  }) ?? true) ? (
                    <config.DetailsTabContent
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      entityId={selectedId!}
                      facets={detail?.facets ?? []}
                      linkedEntities={detail?.linked_entities ?? []}
                    />
                  ) : undefined
                }
              />
            )}
          </DetailPane>
        ) : (
          <DetailPane>
            <div className="flex items-center justify-center h-full text-content-tertiary text-base">
              Select an item to view details
            </div>
          </DetailPane>
        )
      }
    />
    </>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function defaultMapListItem(raw: Record<string, unknown>): ListItem {
  return {
    id: raw.id as string,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    name: (raw.name as string) ?? (raw.title as string) ?? null,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    schema_id: (raw.schema_id as string) ?? "",
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    preview: (raw.preview as string) ?? null,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    timestamp: (raw.timestamp as string) ?? (raw.created_at as string) ?? null,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    avatar_url: (raw.avatar_url as string) ?? null,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    is_pinned: (raw.is_pinned as boolean) ?? undefined,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    is_archived: (raw.is_archived as boolean) ?? undefined,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    unread_count: (raw.unread_count as number) ?? undefined,
  };
}

function initials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}
